import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from "rxjs";
import { ToastService } from "src/app/shared/toasts/toast-service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { FeiraModel } from "src/app/pages/call-center/pages/events/model/event.model";
import { StatusCliente } from "src/app/pages/call-center/pages/status-config/model/status.model";
import { ContactService } from "../../../../contact.service";
import { EventService } from "src/app/pages/call-center/pages/events/event.service";
import { StatusConfigService } from "src/app/pages/call-center/pages/status-config/services/status-config.service";
import { CreateContactListModalComponent } from "../create-contact-list-modal/create-contact-list-modal.component";

@Component({
  selector: "app-create-contact-list-filter",
  templateUrl: "./create-contact-list-filter.component.html",
  styleUrl: "./create-contact-list-filter.component.scss",
})
export class CreateContactListFilterComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  createContactForm!: FormGroup;
  isLoading: boolean = true;
  statusMessages: StatusCliente[] = [];
  showCreateListButton = false;

  // Propriedades para o select de feira com busca e scroll infinito
  feirasFiltradas: FeiraModel[] = [];
  buscaFeira: string = '';
  dropdownFeiraAberto: boolean = false;
  feiraSelecionada: FeiraModel | null = null;
  feiraPaginas = {
    paginaAtual: 1,
    totalPaginas: 1,
    carregandoMais: false,
    temMais: true
  };
  buscaFeiraDebounce$ = new Subject<string>();

  constructor(
    private contactService: ContactService,
    private eventService: EventService,
    private statusConfigService: StatusConfigService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private modalService: NgbModal,
  ) {}
  ngOnInit(): void {
    // Feiras são carregadas sob demanda quando o dropdown é aberto
          this.isLoading = false;

    this.statusConfigService
      .getStatusMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          // Verifica se é um array direto
          if (Array.isArray(status)) {
            this.statusMessages = status;
          } 
          // Verifica se é um objeto com propriedade data ou items
          else if (status && typeof status === 'object') {
            const data = (status as any).data || (status as any).items || (status as any).statusMessages || [];
            this.statusMessages = Array.isArray(data) ? data : [];
          } 
          // Caso contrário, array vazio
          else {
            this.statusMessages = [];
          }
        },
        error: (error) => {
          console.error(error);
          this.statusMessages = [];
        },
      });
    this.createContactForm = this.fb.group({
      MetroQuadradoDe: [""],
      MetroQuadradoAte: [""],
      FeiraId: ["", Validators.required],
      StatusId: [""],
      DataAgendamentoDe: [""],
      DataAgendamentoAte: [""],
    });

    // Carregar feira selecionada se já existir no formulário
    this.createContactForm.get('FeiraId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((feiraId) => {
        if (feiraId && this.feiraSelecionada?.id !== feiraId) {
          // Se o ID mudou mas não temos a feira selecionada, buscar
          if (!this.feiraSelecionada || this.feiraSelecionada.id !== feiraId) {
            // A feira será carregada quando o dropdown for aberto
          }
        }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.buscaFeiraDebounce$.complete();
  }

  onFilter() {
    if (this.createContactForm.invalid) {
      this.toastService.show("Feira é obrigatório", {
        classname: "bg-warning text-light",
      });
      return;
    }
    const filters = this.createContactForm.value;
    this.contactService.setFilters({ ...filters });
    this.showCreateListButton = true;
  }

  openCreateListModal(): void {
    const modalRef = this.modalService.open(CreateContactListModalComponent);
    modalRef.closed.subscribe({
      next: () => {},
      error: (error) => {
        console.error(error);
      },
    });
  }

  // Métodos para o select de feira com busca e scroll infinito
  abrirDropdownFeira(): void {
    this.dropdownFeiraAberto = true;
    
    // Se não há feiras carregadas, carregar inicialmente
    if (!this.feirasFiltradas || this.feirasFiltradas.length === 0) {
      this.carregarFeiras(true);
    }
  }

  fecharDropdownFeira(): void {
    // Delay para permitir clique nos itens
    setTimeout(() => {
      this.dropdownFeiraAberto = false;
    }, 200);
  }

  filtrarFeiras(): void {
    // Resetar paginação ao filtrar
    this.feiraPaginas.paginaAtual = 1;
    this.carregarFeiras(true);
    
    // Abrir dropdown automaticamente quando há busca
    if (!this.dropdownFeiraAberto) {
      this.dropdownFeiraAberto = true;
    }
  }

  carregarFeiras(resetar: boolean = true): void {
    if (resetar) {
      this.feiraPaginas = {
        paginaAtual: 1,
        totalPaginas: 1,
        carregandoMais: false,
        temMais: true
      };
      this.feirasFiltradas = [];
    }
    
    if (this.feiraPaginas.carregandoMais || !this.feiraPaginas.temMais) {
      return;
    }
    
    const termo = this.buscaFeira || '';
    
    if (resetar) {
      this.feiraPaginas.carregandoMais = true;
    }
    
    const filtro = {
      nome: termo,
      paginaAtual: this.feiraPaginas.paginaAtual.toString(),
      quantidadePorPagina: '20'
    };
    
    this.eventService.consultarFeira(filtro)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const novasFeiras = response.feiras || [];
          
          if (resetar) {
            this.feirasFiltradas = novasFeiras;
          } else {
            this.feirasFiltradas = [...this.feirasFiltradas, ...novasFeiras];
          }
          
          this.feiraPaginas.totalPaginas = response.paginacao?.ultimaPagina || 1;
          this.feiraPaginas.temMais = this.feiraPaginas.paginaAtual < this.feiraPaginas.totalPaginas;
          this.feiraPaginas.carregandoMais = false;
        },
        error: (error) => {
          console.error('Erro ao carregar feiras:', error);
          this.toastService.show('Erro ao carregar feiras', {
            classname: 'bg-danger text-light',
          });
          this.feiraPaginas.carregandoMais = false;
        }
      });
  }

  carregarMaisFeiras(): void {
    if (this.feiraPaginas.carregandoMais || !this.feiraPaginas.temMais) {
      return;
    }
    
    this.feiraPaginas.paginaAtual++;
    this.carregarFeiras(false);
  }

  onScrollFeiras(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // Quando está próximo do final (90% da altura)
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      this.carregarMaisFeiras();
    }
  }

  selecionarFeira(feira: FeiraModel): void {
    // Atualizar o formulário com o ID da feira
    this.createContactForm.get('FeiraId')?.setValue(feira.id);
    
    // Atualizar o campo de busca com o nome da feira selecionada
    this.buscaFeira = feira.nome;
    this.feiraSelecionada = feira;
    
    // Fechar dropdown
    this.dropdownFeiraAberto = false;
  }

  trackByFeira(index: number, feira: FeiraModel): string {
    return feira.id || index.toString();
  }

  get isStatusMessagesArray(): boolean {
    return Array.isArray(this.statusMessages);
  }
}
